// src/components/compliance/faceGuide.ts
/**
 * Face guide of the KYC selfie (Issue #1119).
 *
 * The selfie is taken in a nearly square cut-out with an oval on the left the
 * face belongs into and room for the hand on the right (Issue #1150). This
 * module answers two questions for the live view: *is* there a face, and does
 * the one face sit in the oval the way it should. Only then is the shutter
 * released — by itself, so nobody has to press a button while holding the
 * phone at arm's length.
 *
 * Nothing leaves the browser. The detection runs on the local video frames;
 * loaded from the network is only the code and — without the browser API — the
 * model file. No biometric data is sent anywhere, the recognition merely guides
 * the capture (there is no face comparison on the server).
 *
 * Two detectors, in this order:
 *  1. the browser's `FaceDetector` (Shape Detection API), where it exists —
 *     nothing to download at all;
 *  2. otherwise MediaPipe, pulled in through a *dynamic* import so the WASM
 *     bundle never reaches the main chunk and is only fetched in the selfie
 *     step.
 *
 * Everything except {@link createFaceDetector} is plain arithmetic without the
 * DOM — the geometry has to be checkable in a unit test, not only in front of a
 * real camera.
 */
import type { CropRect } from './cameraCrop'

/**
 * Rectangle of a face or of the oval, in the coordinates of the visible
 * cut-out of the live view. Both have to be measured in the same system —
 * the caller shifts the detected faces accordingly.
 */
export type Box = CropRect

/** What the live view has to say about the current picture. */
export type FaceGuideState =
  | 'none'
  | 'multiple'
  | 'off-center'
  | 'too-small'
  | 'too-large'
  | 'ok'

/**
 * The detector as the live view uses it — the same shape for the browser API
 * and for MediaPipe. `detect` never rejects: a failed frame yields no faces,
 * the loop simply carries on with the next one.
 */
export interface FaceDetectorLike {
  detect(video: HTMLVideoElement, timestampMs: number): Promise<Box[]>
  /** Frees the model; MediaPipe holds WASM memory the GC does not touch. */
  close(): void
}

/** Roughly five pictures per second — enough to feel instant, cheap on battery. */
export const FACE_DETECT_INTERVAL_MS = 200

/** How long we wait for a detector before the guide stays off. */
export const FACE_DETECTOR_TIMEOUT_MS = 8_000

/**
 * After this long without a single good frame the shutter button is freed.
 *
 * The detection is a help, never a barrier: bad light, a mask, an unusual
 * camera — nobody may end up unable to finish the identification.
 */
export const FACE_MANUAL_FALLBACK_MS = 15_000

/** How long the face has to sit in the oval before the shutter is released. */
export const FACE_STEADY_MS = 1_000

/** How far the middle of the face may sit off the middle of the oval. */
export const FACE_CENTER_TOLERANCE = 0.15

/** Accepted height of the face, as a share of the oval height. */
export const FACE_MIN_HEIGHT_SHARE = 0.45
export const FACE_MAX_HEIGHT_SHARE = 0.95

/** Size of the oval inside the live view, as a share of its box. */
export const FACE_OVAL_WIDTH_SHARE = 0.42
export const FACE_OVAL_HEIGHT_SHARE = 0.7

/**
 * Where the middle of the oval sits, as a share of the box width (Issue #1150).
 *
 * Left of the middle, not in it: the finger task (Issue #1144) needs the hand in
 * the very same picture, and next to the face instead of over it. The right
 * third is therefore left to {@link HAND_GUIDE}.
 */
export const FACE_OVAL_CENTER_X_SHARE = 0.32

/**
 * Where the hand belongs during the finger task (Issue #1150) — shares of the
 * box, like the oval.
 *
 * Shown only, never detected: the fingers are counted by the review on the
 * stored picture (api#1045), not in the browser. The outline is there so the
 * customer sees where the hand has to be — exactly as the oval does for the face.
 */
export const HAND_GUIDE = {
  centerXShare: 0.76,
  centerYShare: 0.5,
  widthShare: 0.34,
  heightShare: 0.55,
} as const

/**
 * How many faces the browser detector is asked for. More than one is the whole
 * point — a second person in the picture has to be noticed, not cut off.
 */
export const FACE_MAX_DETECTIONS = 4

/**
 * WASM files of MediaPipe, served by jsDelivr.
 *
 * The version has to match `@mediapipe/tasks-vision` in `package.json`: the
 * loader and the binary belong to the same build.
 */
export const FACE_WASM_BASE_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm'

/** BlazeFace, short range — made for a face at arm's length in front of a phone. */
export const FACE_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite'

// ---------------------------------------------------------------------------
// Geometry — pure, no detector
// ---------------------------------------------------------------------------

function isPositive(value: number): boolean {
  return Number.isFinite(value) && value > 0
}

/**
 * The oval inside a box of `boxWidth × boxHeight`: upright in the middle of the
 * height, around {@link FACE_OVAL_CENTER_X_SHARE} of the width.
 *
 * The overlay and the evaluation come from this one function; a second
 * calculation in the component would sooner or later drift apart from what is
 * on screen. `null` without dimensions (jsdom, first frame).
 */
export function faceOvalRect(boxWidth: number, boxHeight: number): Box | null {
  if (![boxWidth, boxHeight].every(isPositive)) return null

  const width = boxWidth * FACE_OVAL_WIDTH_SHARE
  const height = boxHeight * FACE_OVAL_HEIGHT_SHARE
  return {
    x: boxWidth * FACE_OVAL_CENTER_X_SHARE - width / 2,
    y: (boxHeight - height) / 2,
    width,
    height,
  }
}

/**
 * What the live view should say about the faces it found.
 *
 * Exactly one face, its middle within {@link FACE_CENTER_TOLERANCE} of the
 * middle of the oval, its height between {@link FACE_MIN_HEIGHT_SHARE} and
 * {@link FACE_MAX_HEIGHT_SHARE} of the oval height. Anything else names the
 * one thing to change — a hint that says «wrong» without saying what helps
 * nobody.
 *
 * The position is judged before the size: someone standing next to the oval is
 * told to move into it first, otherwise «closer» would send them towards a
 * spot that is wrong anyway.
 */
export function evaluateFaces(faces: readonly Box[], oval: Box): FaceGuideState {
  if (faces.length === 0) return 'none'
  if (faces.length > 1) return 'multiple'

  const face = faces[0]
  // An unusable measurement counts as «no face» — never as a reason to release
  if (![oval.width, oval.height, face.width, face.height].every(isPositive)) return 'none'

  const offX = Math.abs(face.x + face.width / 2 - (oval.x + oval.width / 2))
  const offY = Math.abs(face.y + face.height / 2 - (oval.y + oval.height / 2))
  if (offX > oval.width * FACE_CENTER_TOLERANCE || offY > oval.height * FACE_CENTER_TOLERANCE) {
    return 'off-center'
  }

  const share = face.height / oval.height
  if (share < FACE_MIN_HEIGHT_SHARE) return 'too-small'
  if (share > FACE_MAX_HEIGHT_SHARE) return 'too-large'
  return 'ok'
}

/**
 * Watches whether the face stays where it belongs.
 *
 * The clock starts with the first `'ok'` and runs as long as no other state
 * comes in between; anything else drops it back to zero. A single lucky frame
 * must not release the shutter — a blink or a passer-by would be enough.
 */
export class FaceStability {
  private since: number | null = null

  /** Forgets the running time, e.g. when the live view comes back. */
  reset(): void {
    this.since = null
  }

  /** `true` once the state has been `'ok'` for {@link FACE_STEADY_MS}. */
  push(state: FaceGuideState, now: number): boolean {
    if (state !== 'ok') {
      this.since = null
      return false
    }
    if (this.since === null) {
      this.since = now
      return false
    }
    return now - this.since >= FACE_STEADY_MS
  }
}

// ---------------------------------------------------------------------------
// The browser's own detector (Shape Detection API)
// ---------------------------------------------------------------------------

interface NativeFaceDetection {
  boundingBox?: { x: number; y: number; width: number; height: number }
}

interface NativeFaceDetector {
  detect(source: HTMLVideoElement): Promise<readonly NativeFaceDetection[]>
}

type NativeFaceDetectorConstructor = new (options?: {
  maxDetectedFaces?: number
  fastMode?: boolean
}) => NativeFaceDetector

/**
 * The browser's `FaceDetector`, wrapped — or `null` where the API is missing.
 *
 * Only Chrome on Android ships it today, and there it is by far the cheapest
 * option: no download, no WASM, the detection sits in the browser already.
 */
function nativeFaceDetector(): FaceDetectorLike | null {
  if (typeof window === 'undefined' || !('FaceDetector' in window)) return null

  const Constructor = (window as { FaceDetector?: NativeFaceDetectorConstructor }).FaceDetector
  if (typeof Constructor !== 'function') return null

  try {
    const detector = new Constructor({ maxDetectedFaces: FACE_MAX_DETECTIONS, fastMode: true })
    return {
      detect: (video) =>
        detector.detect(video).then(
          (found) => found.map(nativeBox).filter((box): box is Box => box !== null),
          // A failed frame is not an error the customer has to hear about
          () => [],
        ),
      // Nothing to free — the browser owns the detector
      close: () => {},
    }
  } catch {
    // Constructor refused (unsupported options, insecure context) — no guide
    return null
  }
}

function nativeBox(detection: NativeFaceDetection): Box | null {
  const box = detection.boundingBox
  if (!box) return null
  return { x: box.x, y: box.y, width: box.width, height: box.height }
}

// ---------------------------------------------------------------------------
// MediaPipe — only where the browser has no detector of its own
// ---------------------------------------------------------------------------

interface MediaPipeDetection {
  boundingBox?: { originX: number; originY: number; width: number; height: number }
}

interface MediaPipeFaceDetector {
  detectForVideo(video: HTMLVideoElement, timestampMs: number): { detections: MediaPipeDetection[] }
  close(): void
}

/**
 * The small part of the MediaPipe API we depend on.
 *
 * Declared here instead of using the package types: a local interface keeps the
 * module mockable in the tests (jsdom has neither WASM nor network) and
 * documents exactly which calls the guide relies on.
 */
interface MediaPipeVision {
  FilesetResolver: { forVisionTasks(basePath: string): Promise<unknown> }
  FaceDetector: {
    createFromOptions(
      fileset: unknown,
      options: {
        baseOptions: { modelAssetPath: string }
        runningMode: 'VIDEO'
      },
    ): Promise<MediaPipeFaceDetector>
  }
}

/** MediaPipe is published as an ES module; a CJS interop may nest it in `default`. */
function asVision(imported: unknown): MediaPipeVision | null {
  const candidates: unknown[] = [imported, (imported as { default?: unknown } | null)?.default]
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue
    const module = candidate as Partial<MediaPipeVision>
    if (
      typeof module.FilesetResolver?.forVisionTasks === 'function' &&
      typeof module.FaceDetector?.createFromOptions === 'function'
    ) {
      return module as MediaPipeVision
    }
  }
  return null
}

function mediaPipeBox(detection: MediaPipeDetection): Box | null {
  const box = detection.boundingBox
  if (!box) return null
  return { x: box.originX, y: box.originY, width: box.width, height: box.height }
}

async function loadMediaPipeDetector(): Promise<FaceDetectorLike | null> {
  let vision: MediaPipeVision | null = null
  try {
    vision = asVision(await import('@mediapipe/tasks-vision'))
  } catch {
    // Import blocked (offline, old browser, CSP) — the guide stays off
    return null
  }
  if (!vision) return null

  try {
    const fileset = await vision.FilesetResolver.forVisionTasks(FACE_WASM_BASE_URL)
    const detector = await vision.FaceDetector.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: FACE_MODEL_URL },
      runningMode: 'VIDEO',
    })

    // MediaPipe insists on strictly rising timestamps, and a browser may hand
    // the same frame time to two callbacks — hence the running counter.
    let lastTimestamp = -1
    return {
      detect: (video, timestampMs) => {
        const stamp = Math.max(Math.round(timestampMs), lastTimestamp + 1)
        lastTimestamp = stamp
        try {
          const result = detector.detectForVideo(video, stamp)
          return Promise.resolve(
            result.detections.map(mediaPipeBox).filter((box): box is Box => box !== null),
          )
        } catch {
          return Promise.resolve([])
        }
      },
      close: () => {
        try {
          detector.close()
        } catch {
          // Already released — nothing left to do
        }
      },
    }
  } catch {
    // No model, no WASM, no GPU — the shutter stays manual
    return null
  }
}

/**
 * Resolves with `null` after `ms` instead of waiting forever.
 *
 * A detector that arrives late is closed right away: it would otherwise hold
 * its WASM memory for the rest of the session without anybody using it.
 */
function withTimeout(
  promise: Promise<FaceDetectorLike | null>,
  ms: number,
): Promise<FaceDetectorLike | null> {
  return new Promise<FaceDetectorLike | null>((resolve) => {
    let settled = false
    const timer = setTimeout(() => {
      settled = true
      resolve(null)
    }, ms)
    promise.then(
      (detector) => {
        clearTimeout(timer)
        if (settled) detector?.close()
        else resolve(detector)
      },
      () => {
        clearTimeout(timer)
        if (!settled) resolve(null)
      },
    )
  })
}

/**
 * A detector for the live view, or `null` when none can be had.
 *
 * `null` is a normal outcome, not a failure: the selfie step then shows no
 * guide and the shutter button works exactly as it did before. The result is
 * deliberately not cached — the detector is closed with the step, and a second
 * attempt may well succeed on a better connection.
 */
export async function createFaceDetector(
  timeoutMs: number = FACE_DETECTOR_TIMEOUT_MS,
): Promise<FaceDetectorLike | null> {
  const native = nativeFaceDetector()
  if (native) return native
  return withTimeout(loadMediaPipeDetector(), timeoutMs)
}
